import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query"
import type { InferResponseType } from "hono/client"
import { client } from "@/api"

// Events tab: what people did that a response (backend lib/responses) is ready for.
//
// The response type is INFERRED from the route, so a field renamed on the backend breaks
// this file at typecheck instead of rendering `undefined` in the table.
export type ResponseEventsResponse = InferResponseType<typeof client.admin.responses.events.$get>
export type ResponseEvent = ResponseEventsResponse["events"][number]

/**
 * Fetched when the tab opens and when Refresh is pressed. It never polls and never
 * refetches on focus: the list is a handful of window-bounded D1 queries, and an idle
 * tab must not keep paying for them (the no-idle-polling rule).
 */
export function useResponseEvents() {
  const queryClient = useQueryClient()
  const queryKey = ["admin", "response-events"]
  const result = useInfiniteQuery({
    queryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const response = await client.admin.responses.events.$get({ query: pageParam ? { cursor: pageParam } : {} })
      if (!response.ok) throw new Error("Unable to load events. Refresh to restart.")
      return response.json()
    },
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
  const events = new Map<string, ResponseEvent>()
  for (const page of result.data?.pages ?? []) {
    for (const event of page.events) events.set(`${event.response_id}:${event.template_ref}`, event)
  }
  return {
    ...result,
    data: result.data ? { events: [...events.values()].sort((a, b) => a.hours_ago - b.hours_ago), generated_at: result.data.pages[0].generated_at } : undefined,
    refetch: () => queryClient.resetQueries({ queryKey, exact: true }),
  }
}
